<template>
  <div class="py-6">
    <div v-if="loading" class="flex justify-center py-8">
      <el-skeleton :rows="4" animated />
    </div>

    <el-alert
      v-else-if="error"
      type="error"
      :title="i18n.t('errorLoadingData')"
      :description="error.message"
      show-icon
      :closable="false">
      <el-button @click="retryFetch">
        {{ i18n.t('retry') }}
      </el-button>
    </el-alert>

    <div
      v-else-if="!personStats"
      class="flex flex-col items-center justify-center py-8">
      <EmptyState
        :icon="User"
        :title="i18n.t('browserExtension.noPersonDataTitle')"
        :text="i18n.t('browserExtension.noPersonDataDescription')"
        :show-docs-btn="false" />
    </div>

    <div v-else class="space-y-8">
      <!-- 1. Profile Header -->
      <div class="flex items-center gap-5">
        <div>
          <div class="flex min-w-0 flex-col">
            <TextMultiline
              v-if="visiblePersonEmail"
              :text="visiblePersonEmail"
              tag="h2"
              class="text-xl font-bold text-gray-900 dark:text-white" />
            <div class="flex items-center gap-2">
              <TextMultiline
                :text="props.installationId"
                tag="h2"
                class="font-mono"
                :class="
                  visiblePersonEmail
                    ? 'text-base font-medium text-contrast-muted'
                    : 'text-xl font-bold text-gray-900 dark:text-white'
                " />

              <button
                class="focus-visible:outline-blue-500 cursor-pointer text-gray-400 transition-colors hover:text-gray-600 focus-visible:outline-2 focus-visible:outline-offset-2 active:scale-95 dark:hover:text-gray-300"
                :title="i18n.t('browserExtension.copy')"
                :aria-label="
                  i18n.t(
                    'browserExtension.copyInstallationId',
                    'Copy installation ID to clipboard'
                  )
                "
                @click="copyId">
                <Copy class="h-4 w-4" />
              </button>
            </div>
          </div>
          <div class="mt-1 flex items-center gap-3 text-sm text-gray-500">
            <span class="flex items-center gap-1">
              <Clock class="h-4 w-4" />
              {{ i18n.t('browserExtension.lastSeen') }}:
              {{ displayDate(new Date(lastSeen)) }}
            </span>
          </div>
        </div>
      </div>

      <!-- 2. KPI Cards -->
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiCard
          :title="i18n.t('browserExtension.totalSessions')"
          :value="personStats.metadata.total_sessions"
          :icon="Activity" />
        <KpiCard
          :title="i18n.t('browserExtension.activeDays')"
          :value="personStats.metadata.active_days"
          :icon="Calendar" />
        <KpiCard
          :title="i18n.t('browserExtension.uniqueTools')"
          :value="personStats.metadata.unique_tools"
          :icon="Wrench" />
      </div>

      <!-- 3. Top Tools (New) -->
      <el-card v-if="personStats.tools?.length" shadow="never">
        <div class="mb-6">
          <h3 class="text-base font-semibold text-gray-900 dark:text-white">
            {{ i18n.t('browserExtension.topTools') }}
          </h3>
        </div>
        <div class="space-y-6">
          <div
            v-for="(tool, index) in topToolsLimited"
            :key="tool.software_name"
            class="focus-visible:outline-blue-500 group -mx-2 cursor-pointer rounded-lg p-2 transition-colors hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-2 dark:hover:bg-gray-700/50"
            tabindex="0"
            role="button"
            @click="$emit('tool-click', tool.software_name)"
            @keyup.enter="$emit('tool-click', tool.software_name)">
            <div class="mb-2 flex items-center justify-between">
              <div class="flex items-center gap-3">
                <span
                  class="flex h-6 w-6 items-center justify-center rounded-full bg-gray-50 text-xs font-medium text-gray-500 ring-1 ring-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-700">
                  {{ index + 1 }}
                </span>
                <span
                  class="group-hover:text-blue-600 text-sm font-medium text-gray-900 transition-colors dark:text-white">
                  {{ tool.software_name }}
                </span>
              </div>
              <span class="text-xs font-medium text-gray-500">
                {{ tool.total_sessions }}
                <span class="font-normal text-gray-400">{{
                  i18n.t('browserExtension.sessions')
                }}</span>
              </span>
            </div>
            <!-- Progress Bar -->
            <div
              class="relative h-2 w-full overflow-hidden rounded-full bg-gray-50 dark:bg-gray-800">
              <div
                class="absolute left-0 top-0 h-full rounded-full transition-all duration-500 ease-out"
                :style="{
                  width: `${(tool.total_sessions / maxToolSessions) * 100}%`,
                  backgroundColor: index === 0 ? '#2563eb' : '#60a5fa',
                  opacity: index === 0 ? 1 : 0.8,
                }"></div>
            </div>
          </div>
        </div>
      </el-card>

      <!-- 4. Daily Usage Heatmap -->
      <el-card v-if="hasChartData" shadow="never">
        <div class="mb-6 flex items-center justify-between">
          <h3 class="text-base font-semibold text-gray-900 dark:text-white">
            {{ i18n.t('browserExtension.dailyUsage') }}
          </h3>
          <SmSelect
            v-if="toolOptions.length > 1"
            v-model:selection="selectedToolOption"
            :options="toolOptions"
            class="w-40"
            size="small"
            :clearable="false">
            <template #item="{ item }">
              <span :class="{ 'font-medium': item.value === 'all' }">
                {{ item.label }}
              </span>
            </template>
          </SmSelect>
        </div>

        <div class="relative">
          <Transition name="fade">
            <div
              v-if="loadingChart"
              class="bg-white/80 absolute inset-0 z-10 flex items-center justify-center backdrop-blur-sm dark:bg-gray-800/80">
              <div
                class="animate-spin border-blue-600 border-t-transparent h-8 w-8 rounded-full border-2"></div>
            </div>
          </Transition>
          <DailyUsageHeatmap
            :data="dailyUsageData"
            :from-date="fromDate"
            :till-date="tillDate" />
        </div>
      </el-card>
      <el-card v-else shadow="never">
        <div
          class="flex min-h-[200px] flex-col items-center justify-center py-8">
          <BarChart2 class="mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
          <p class="text-sm font-medium text-gray-500 dark:text-gray-400">
            {{ i18n.t('noChartDataDescription') }}
          </p>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useClipboard } from '@vueuse/core'
  import {
    User,
    Activity,
    Calendar,
    Wrench,
    Clock,
    Copy,
    BarChart2,
  } from 'lucide-vue-next'
  import EmptyState from '@/components/shared/EmptyState.vue'
  import SmSelect from '@/components/sm/SmSelect.vue'
  import KpiCard from '../../dashboard/widgets/KpiCard.vue'
  import DailyUsageHeatmap from '../../dashboard/widgets/DailyUsageHeatmap.vue'
  import { displayDate } from '@/utils/formatter/date'
  import type { DailyUsagePoint } from '@/client'
  import {
    getToolUsageStats,
    PERSON_STATS_KEY,
    type PersonStatsContext,
  } from '@/stores/browserextension/browserextension.store'
  import { sendToast } from '@/components/sm/SmNotification'
  import { inject } from 'vue'
  import { useSessionStore } from '@/stores/sessionStore'

  interface Props {
    installationId: string
    personEmail?: string | null
    fromDate?: Date | null
    tillDate?: Date | null
  }

  const props = defineProps<Props>()
  defineEmits<{
    (e: 'tool-click', toolName: string): void
  }>()
  const i18n = useI18n()
  const sessionStore = useSessionStore()
  const { copy } = useClipboard()

  const browserExtensionEmailEnabled = computed(function () {
    return !!sessionStore.currentCompanyInfo?.features.browser_extension_email
  })

  const visiblePersonEmail = computed(function () {
    if (!browserExtensionEmailEnabled.value || !props.personEmail) {
      return null
    }

    const normalizedEmail = props.personEmail.trim()
    if (!normalizedEmail) {
      return null
    }

    return normalizedEmail
  })

  // Inject shared person stats from parent
  const injected = inject<PersonStatsContext>(PERSON_STATS_KEY)

  // Use injected data
  const personStats = computed(() => injected?.personStats.value ?? null)
  const loading = computed(() => injected?.loading.value ?? false)
  const error = computed(() => injected?.error.value ?? null)

  // State for chart filtering
  const selectedToolOption = ref<
    { label: string; value: string }[] | undefined
  >([{ label: i18n.t('browserExtension.allTools', 'All Tools'), value: 'all' }])
  const dailyUsageData = ref<DailyUsagePoint[]>([])
  const loadingChart = ref<boolean>(false)

  // Copy ID function
  const copyId = async () => {
    await copy(props.installationId)
    sendToast(
      i18n.t('browserExtension.copiedToClipboard', 'Copied to clipboard'),
      i18n.t(
        'browserExtension.installationIdCopied',
        'Installation ID has been copied to your clipboard.'
      ),
      'success',
      2000
    )
  }

  // Retry fetch
  const retryFetch = () => {
    injected?.refetch()
  }

  // Computed Properties
  const lastSeen = computed(() => {
    if (!personStats.value || !personStats.value.daily_usages?.length) return 0
    return (
      personStats.value.daily_usages[personStats.value.daily_usages.length - 1]
        .timestamp * 1000
    )
  })

  const topToolsLimited = computed(() => {
    return personStats.value?.tools?.slice(0, 5) || []
  })

  const maxToolSessions = computed(() => {
    if (!topToolsLimited.value.length) return 1
    return Math.max(...topToolsLimited.value.map((t) => t.total_sessions))
  })

  // Tool Options for Filter
  const toolOptions = computed(() => {
    const tools = personStats.value?.tools || []
    // Sort tools alphabetically or by usage? Usage is probably better (default from backend)
    const opts = tools.map((t) => ({
      label: t.software_name,
      value: t.software_name,
    }))
    return [
      { label: i18n.t('browserExtension.allTools', 'All Tools'), value: 'all' },
      ...opts,
    ]
  })

  // Check if chart data exists
  const hasChartData = computed(
    () => dailyUsageData.value && dailyUsageData.value.length > 0
  )

  // Watch for tool selection changes
  watch(
    () => selectedToolOption.value,
    async (newVal) => {
      const toolName = newVal?.[0]?.value

      // If "All" or empty, reset to person aggregate stats
      if (!toolName || toolName === 'all') {
        if (personStats.value) {
          dailyUsageData.value = personStats.value.daily_usages
        }
        return
      }

      // Fetch specific tool usage
      loadingChart.value = true
      try {
        const toolStats = await getToolUsageStats(
          props.installationId,
          toolName,
          props.fromDate,
          props.tillDate
        )
        dailyUsageData.value = toolStats?.daily_usages || []
      } catch (e) {
        console.error('Failed to fetch tool usage stats:', e)
      } finally {
        loadingChart.value = false
      }
    }
  )

  // Initialize data when personStats loads or date changes
  watch(
    () => personStats.value,
    (stats) => {
      if (stats) {
        // Only reset if currently on "All", otherwise re-fetch the selected tool
        if (
          !selectedToolOption.value ||
          selectedToolOption.value[0].value === 'all'
        ) {
          dailyUsageData.value = stats.daily_usages
        } else {
          // Trigger re-fetch for currently selected tool (date range might have changed)
          // Actually, simply triggering the watcher by re-assigning might work, or call logic directly
          // Let's just reset to 'All' to be safe and simple when dates change drastically
          // OR better: let the user keep their selection.
          const currentTool = selectedToolOption.value[0].value
          getToolUsageStats(
            props.installationId,
            currentTool,
            props.fromDate,
            props.tillDate
          ).then((stats) => {
            dailyUsageData.value = stats?.daily_usages || []
          })
        }
      }
    }
  )
</script>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s ease;
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
</style>
